import React from "react";

import toast from "react-hot-toast";

import { MinimalErrorSVG } from "@/components/icons/MinimalErrorSVG";
import { MinimalSuccessSVG } from "@/components/icons/MinimalSuccessSVG";
import { cn } from "@/lib/utils";

import type { ReactNode } from "react";
import type { ToastOptions, ToastPosition } from "react-hot-toast";

interface Options {
  position?: ToastPosition;
  isWide?: boolean;
  opts?: ToastOptions;
  actions?: ReactNode;
  icon?: ReactNode;
}

interface ToastVariant {
  bg: string;
  border: string;
  text: string;
  defaultIcon: ReactNode;
}

const variants: Record<"success" | "neutral" | "error", ToastVariant> = {
  success: {
    bg: "bg-mint-100",
    border: "border-mint-300",
    text: "text-mint-800",
    defaultIcon: <MinimalSuccessSVG className="w-6 h-6 text-mint-500" />,
  },
  neutral: {
    bg: "bg-ash-100",
    border: "border-ash-300",
    text: "text-ash-800",
    defaultIcon: <MinimalSuccessSVG className="w-6 h-6 text-ash-400" />,
  },
  error: {
    bg: "bg-lava-100",
    border: "border-lava-300",
    text: "text-lava-800",
    defaultIcon: <MinimalErrorSVG className="w-6 h-6 text-lava-500" />,
  },
};

function createToast(variant: ToastVariant) {
  return (message: ReactNode, { position = "bottom-center", isWide = false, actions, icon, opts }: Options = {}) => {
    return toast.custom(
      (t) => (
        <div
          style={{ pointerEvents: "auto", ...(isWide && { width: "70vw", maxWidth: "1280px" }) }}
          className={cn(
            "flex items-center gap-3 justify-between",
            variant.bg,
            variant.border,
            variant.text,
            "border p-6 rounded-3xl shadow-lg",
            "transition-opacity duration-200",
            t.visible ? "opacity-100" : "opacity-0"
          )}
        >
          <div className={cn("flex items-center", icon ? "gap-5" : "gap-3")}>
            {icon ?? variant.defaultIcon}
            <span className="font-bold">{message}</span>
          </div>
          {actions}
        </div>
      ),
      { duration: 2000, position, ...opts }
    );
  };
}

export const reboltToast = {
  success: createToast(variants.success),
  neutral: createToast(variants.neutral),
  error: createToast(variants.error),
  /** Dismiss a toast by its `opts.id` — for clearing a stale error toast once the operation recovers. */
  dismiss: (toastId: string) => toast.dismiss(toastId),
};
